/**
 * Button — the single button primitive for the New UI.
 *
 * Renders a <button> (or any element via `as`) styled by the .cs-btn classes in
 * styles/buttons.css. Replaces the dozens of ad-hoc inline-styled buttons that
 * each re-implemented hover/disabled/focus by hand.
 *
 * Props
 *   variant   'primary' | 'gold' | 'ink' | 'danger' | 'success'
 *             | 'outline' | 'ghost' | 'soft'                  (default 'primary')
 *   size      'xs' | 'sm' | 'md' | 'lg'                       (default 'md')
 *   block     boolean   — full width
 *   iconOnly  boolean   — square icon button (pass a single icon as children)
 *   loading   boolean   — show a spinner and disable the button
 *   leftIcon / rightIcon  — node rendered before/after the label
 *   as        elementType — render a different tag (e.g. 'a', Link); when not a
 *             native <button>, `disabled` is reflected via aria-disabled only
 *   className — extra classes appended after the .cs-btn classes
 *
 * Any other prop (onClick, type, title, disabled, aria-*, data-*) is forwarded.
 */

import { forwardRef } from 'react';

const VARIANTS = new Set([
  'primary', 'gold', 'ink', 'danger', 'success', 'warning',
  'orange', 'purple', 'blue', 'outline', 'ghost', 'soft',
]);
const SIZES = new Set(['xs', 'sm', 'md', 'lg']);

const Button = forwardRef(function Button({
  as: Comp = 'button',
  variant = 'primary',
  size = 'md',
  block = false,
  iconOnly = false,
  loading = false,
  leftIcon = null,
  rightIcon = null,
  className = '',
  type,
  disabled = false,
  children,
  ...rest
}, ref) {
  const v = VARIANTS.has(variant) ? variant : 'primary';
  const s = SIZES.has(size) ? size : 'md';
  const isDisabled = disabled || loading;

  const classes = [
    'cs-btn',
    `cs-btn--${v}`,
    `cs-btn--${s}`,
    block && 'cs-btn--block',
    iconOnly && 'cs-btn--icon',
    loading && 'cs-btn--loading',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const isNativeButton = Comp === 'button';

  return (
    <Comp
      ref={ref}
      className={classes}
      // Native <button> gets a real `disabled`; custom elements (a, Link) can't,
      // so reflect state via aria-disabled for AT + the [aria-disabled] CSS rule.
      {...(isNativeButton
        ? { disabled: isDisabled, type: type || 'button' }
        : { 'aria-disabled': isDisabled || undefined })}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && <span className="cs-btn__spinner" aria-hidden="true" />}
      {!loading && leftIcon != null && (
        <span className="cs-btn__icon" aria-hidden="true">{leftIcon}</span>
      )}
      {/* Wrap plain-text labels so they get the hover-lean / click-dip. Element
          children (e.g. a button with an absolutely-positioned chevron) are
          rendered as-is — wrapping them would create a transformed containing
          block and break their positioning. */}
      {children != null && children !== '' && (
        typeof children === 'string' || typeof children === 'number'
          ? <span className="cs-btn__label">{children}</span>
          : children
      )}
      {!loading && rightIcon != null && (
        <span className="cs-btn__icon" aria-hidden="true">{rightIcon}</span>
      )}
    </Comp>
  );
});

export default Button;
